import React, { useCallback } from 'react';
import { Box, Paper, Typography, IconButton, useMediaQuery, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { CreateMeetingTable } from './CreateMeetingDialog';
import { useTranslation } from 'react-i18next';

export default function CreateMeetingSidePane({ open, onClose, accountId, setFeedback, meetingTitle, linkType, listCacheKey, hideCloseButton = false }) {
    const { t } = useTranslation('shared');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    const handleSuccess = useCallback((event, meetingURL) => {
        onClose(event, meetingURL);
    }, [onClose]);

    const handleCancel = useCallback(() => {
        onClose();
    }, [onClose]);

    // On mobile, render as an overlay that takes full width
    if (isMobile) {
        return (
            <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 10,
                display: open ? 'flex' : 'none',
                flexDirection: 'column',
                bgcolor: 'background.paper',
            }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1.5,
                    borderBottom: 1,
                    borderColor: 'divider',
                }}>
                    <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>{t('createMeeting.dialogTitle')}</Typography>
                    <IconButton onClick={handleCancel} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Box sx={{
                    flex: 1,
                    overflow: 'auto',
                    px: 2,
                    py: 2,
                    '&::-webkit-scrollbar': { display: 'none' },
                    scrollbarWidth: 'none',
                }}>
                    <CreateMeetingTable
                        accountId={accountId}
                        setFeedback={setFeedback}
                        handleSuccess={handleSuccess}
                        handleCancel={handleCancel}
                        meetingTitle={meetingTitle}
                        linkType={linkType}
                        listCacheKey={listCacheKey}
                    />
                </Box>
            </Box>
        );
    }

    // Desktop: side panel behavior
    return (
        <Box sx={{
            width: open ? 500 : 0,
            minWidth: open ? 500 : 0,
            transition: 'width 0.3s ease, min-width 0.3s ease',
            overflow: 'hidden',
            height: '100%',
        }}>
            <Paper
                elevation={0}
                sx={{
                    width: 500,
                    minWidth: 500,
                    height: '100%',
                    borderLeft: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 3,
                    py: 2,
                    borderBottom: 1,
                    borderColor: 'divider',
                }}>
                    <Typography variant="h6">{t('createMeeting.dialogTitle')}</Typography>
                    {!hideCloseButton && (
                        <IconButton onClick={handleCancel} size="small">
                            <CloseIcon />
                        </IconButton>
                    )}
                </Box>
                <Box sx={{
                    flex: 1,
                    overflow: 'auto',
                    px: 3,
                    py: 2,
                    '&::-webkit-scrollbar': { display: 'none' },
                    scrollbarWidth: 'none',
                }}>
                    <CreateMeetingTable
                        accountId={accountId}
                        setFeedback={setFeedback}
                        handleSuccess={handleSuccess}
                        handleCancel={handleCancel}
                        meetingTitle={meetingTitle}
                        linkType={linkType}
                        listCacheKey={listCacheKey}
                    />
                </Box>
            </Paper>
        </Box>
    );
}
